import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { CartContext } from './CartContext';
import { API_BASE_URL } from '../utils/media';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const cartContext = useContext(CartContext); // This might be null if not wrapped

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const decodedUser = jwtDecode(storedToken);
          // Check if the token is expired
          if (decodedUser.exp * 1000 < Date.now()) {
            localStorage.removeItem('token');
            setUser(null);
            setToken(null);
            setUserProfile(null);
          } else {
            // Set user and token first
            axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            setUser(decodedUser);
            setToken(storedToken);
            // Then fetch profile
            await fetchUserProfile(decodedUser.id);
          }
        } catch (error) {
          // If token is invalid, clear it
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setUserProfile(null);
        }
      } else {
        // Ensure state is clean if no token is found
        setUserProfile(null);
      }
      setLoading(false);
    };

    initializeAuth();
  }, []); // This effect should only run once on initial app load

  const login = async (email, password) => {
    const response = await axios.post(`${API_BASE_URL}/api/auth/login`, { email, password });
    const { token } = response.data;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const decodedUser = jwtDecode(token);
    await fetchUserProfile(decodedUser.id);
    setUser(decodedUser);
    setToken(token);
    return response;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_BASE_URL}/api/auth/register`, userData);
    // After successful registration, automatically log the user in
    await login(userData.email, userData.password);
    return response;
  };

  const fetchUserProfile = async (userId) => {
    if (!userId) return; // Safeguard against undefined userId
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/profile/${userId}`);
      setUserProfile(response.data);
    } catch (error) {
      console.error("Failed to fetch user profile", error);
      // Handle error, maybe logout user if profile is essential
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    setUserProfile(null);
    if (cartContext) cartContext.clearCart();
  };

  return (
    <AuthContext.Provider value={{ user, userProfile, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
